import React, { useCallback, useEffect } from 'react'
import { usePermissions } from '../../../../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import useUrlFilters from '../../../../hooks/useUrlFilters'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'
import useGenericFilter from '../../../../hooks/filters/useGenericFilter'
import useNotification from '../../../../hooks/useNotification'
import useBaseService from '../../../../hooks/services/useBaseService'
import useAction from '../../../../hooks/useAction'
import { entities } from '../../../../constants/entities'
import MainLayout from '../../../../layouts/MainLayout'
import PageHeader from '../../../../components/PageHeader'
import ListHeader from '../../../../components/ListHeader'
import ConfirmationModal from '../../../../components/modals/ConfirmationModal'
import TableWithInactiveFilter from '../../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../../components/FilterCollapse'
import { inputsFiltersBrands } from './filters/filters-brands'
import useQueryWithLoader from '../../../../hooks/useQueryWithLoader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useLoader from '../../../../hooks/useLoader'
import { faEdit, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons'

const BrandsPage = () => {
  const { canAccess } = usePermissions()
  const { showLoader, hideLoader } = useLoader()

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    'almox_brands'
  )

  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues)
      applyUrlFilters(transformed)
    },
    [applyUrlFilters]
  )

  const handleFilterClear = useCallback(
    (initialLocalFilterState) => {
      const transformedInitial = transformFiltersForUrl(initialLocalFilterState)
      applyUrlFilters(transformedInitial)
    },
    [applyUrlFilters]
  )

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const {
    get: baseServiceGet,
    del: baseServiceDelete,
    setFormErrors,
  } = useBaseService(navigate)
  const { showNotification } = useNotification()

  const {
    data: brandsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['almox_brands', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.brands.get, params)
      return response.result
    },
    staleTime: 60 * 1000, // 60 segundos
    select: (data) => {
      return {
        brands: data.data.map((condition) => ({
          id: condition.id,
          name: condition.name,
          isDeleted: !!condition.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (previousData) => previousData,
  })

  const { brands = [], totalPages = 1 } = brandsData || {}

  const columns = [{ key: 'name', label: 'Nome' }]

  useEffect(() => {
    if (isError) {
      console.error('Erro ao carregar marcas (useQuery):', error)
      showNotification(
        'error',
        `Erro ao carregar marcas: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  const inactivateBrandMutation = useMutation({
    mutationFn: async (id) => {
      showLoader()
      return await baseServiceDelete(entities.brands.delete(id))
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        queryClient.invalidateQueries(['almox_brands'])
      }
    },
    onError: (error) => {
      console.log('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const activateProductMutation = inactivateBrandMutation

  const actions = [
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar marcas',
      onClick: (brand) =>
        navigate(`/almoxarifado/produtos/marcas/editar/${brand.id}`),
      show: (brand) => !brand.isDeleted && canAccess('Atualizar marcas'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir marcas',
      onClick: (brand) =>
        showConfirmation(
          brand,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateBrandMutation.mutateAsync(brand.id)
            } catch (error) {
              console.log('Erro ao excluir produto:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (brand) =>
        !brand.isDeleted &&
        !brand.is_in_transaction &&
        canAccess('Excluir marcas'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir marcas',
      onClick: (brand) =>
        showConfirmation(
          brand,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateProductMutation.mutateAsync(brand.id)
            } catch (error) {
              console.log('Erro ao restaurar marca:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (brand) => brand.isDeleted && canAccess('Excluir marcas'),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Marcas" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersBrands({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Marcas"
          buttonText={'Cadastrar Marca'}
          buttonLink={'/almoxarifado/produtos/marcas/criar'}
          permission={'Criar marcas'}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={brands}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ''}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default BrandsPage
